import React, { useEffect, useRef, useState } from 'react';
import { FONT_MONO, FONT_SERIF } from '../../views/landing/landingData';
import { studioService, getPublishedState, type PublishedState } from '../../services/studio.service';
import { BrandBtn, CloseX, DangerNote, Eyebrow, GhostBtn, Icons, Modal, PUBLISH_COST, Spinner, fmtCredits } from './studioUi';

interface PublishDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onPublish: (subdomain: string) => Promise<{ success: boolean; url?: string; error?: string }>;
    credits: number;
    /** Needed to look up the current publish state and to allow reusing this app's own domain. */
    applicationId: string;
    /** Opens the top-up dialog when the balance is short. */
    onTopUp: () => void;
    /** Fired after a successful publish so the shell can refresh its published state. */
    onPublished?: () => void;
    /** Opens the custom-domain dialog (connect or buy a .com / .id). */
    onOpenDomains?: () => void;
}

type SlugStatus = 'idle' | 'short' | 'checking' | 'free' | 'taken';

/**
 * "Publish your site" from the handoff design. Two stages: the subdomain form
 * (with the republish choice for apps that are already live) and "Your site is
 * live" with the URL the SERVER built — never reconstructed here, since
 * published apps resolve by host at the Caddy edge.
 */
export const PublishDialog: React.FC<PublishDialogProps> = ({ open, onOpenChange, onPublish, credits, applicationId, onTopUp, onPublished, onOpenDomains }) => {
    const [slug, setSlug] = useState('');
    const [status, setStatus] = useState<SlugStatus>('idle');
    // Real published-app domain, supplied by check-subdomain. 'hyvay.com' only fills
    // the first render, before the first availability check answers.
    const [appDomain, setAppDomain] = useState('hyvay.com');
    const [existing, setExisting] = useState<PublishedState | null>(null);
    const [mode, setMode] = useState<'reuse' | 'new'>('new');
    const [publishing, setPublishing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [liveUrl, setLiveUrl] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const checkTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const close = () => { if (!publishing) onOpenChange(false); };

    // On open: reset, then discover whether this app is already live (default: reuse).
    useEffect(() => {
        if (!open) return;
        setSlug(''); setStatus('idle'); setMode('new'); setPublishing(false); setError(null); setLiveUrl(null); setCopied(false);
        let cancelled = false;
        void (async () => {
            const state = await getPublishedState(applicationId);
            if (cancelled) return;
            setExisting(state);
            if (state) {
                setMode('reuse');
                setSlug(state.subdomain);
                setStatus('free'); // its own address is by definition usable
                const host = state.url.replace(/^https?:\/\//, '').split('/')[0].split(':')[0];
                const dom = host.startsWith(`${state.subdomain}.`) ? host.slice(state.subdomain.length + 1) : null;
                if (dom) setAppDomain(dom);
            }
        })();
        return () => { cancelled = true; };
    }, [open, applicationId]);

    useEffect(() => () => {
        if (checkTimer.current) clearTimeout(checkTimer.current);
        if (copyTimer.current) clearTimeout(copyTimer.current);
    }, []);

    const onSlug = (raw: string) => {
        const next = raw.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-{2,}/g, '-');
        setSlug(next);
        setError(null);
        if (checkTimer.current) clearTimeout(checkTimer.current);
        if (!next.trim()) { setStatus('idle'); return; }
        if (next.length < 3) { setStatus('short'); return; }
        setStatus('checking');
        checkTimer.current = setTimeout(async () => {
            const { available, domain } = await studioService.checkSubdomainAvailability(next, applicationId);
            if (domain) setAppDomain(domain);
            setStatus(available ? 'free' : 'taken');
        }, 600);
    };

    const canAfford = credits >= PUBLISH_COST;
    const reusing = !!existing && mode === 'reuse';
    const ready = status === 'free' && !!slug.trim() && canAfford && !publishing;

    const confirm = async () => {
        if (!ready) return;
        setPublishing(true);
        setError(null);
        const result = await onPublish(slug);
        setPublishing(false);
        if (result.success && result.url) {
            setLiveUrl(result.url);
            onPublished?.();
        } else if (result.success) {
            setError('The site was published, but the server did not return its URL.');
            onPublished?.();
        } else {
            setError(result.error || 'Publishing failed. Please try again.');
        }
    };

    const copyLive = async () => {
        if (!liveUrl) return;
        try { await navigator.clipboard.writeText(liveUrl); } catch { /* ignore */ }
        setCopied(true);
        if (copyTimer.current) clearTimeout(copyTimer.current);
        copyTimer.current = setTimeout(() => setCopied(false), 1800);
    };

    const slugBorder = status === 'taken' ? 'var(--hv-danger)' : status === 'free' && slug ? 'var(--hv-leaf)' : 'var(--hv-line)';
    const slugMsg: Record<SlugStatus, string> = {
        idle: 'Pick a subdomain to continue.',
        short: 'Use at least 3 characters.',
        checking: 'Checking availability…',
        free: reusing ? 'This is the address your site uses today.' : 'Subdomain is available.',
        taken: 'That subdomain is taken. Try another one.',
    };
    const slugMsgFg = status === 'taken' ? 'var(--hv-danger)' : status === 'free' && slug ? 'var(--hv-leaf)' : 'var(--hv-muted)';
    const preview = `${slug.trim() || 'your-site-name'}.${appDomain}`;

    const optionRow = (active: boolean, onPick: () => void, title: string, sub: string) => (
        <button type="button" onClick={onPick} className={active ? '' : 'hv-h-page2-bd'} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%', padding: '12px 14px', borderRadius: 14, textAlign: 'left', cursor: 'pointer', border: `1.5px solid ${active ? 'var(--brand,#13422C)' : 'var(--hv-line)'}`, background: active ? 'var(--hv-mint)' : 'transparent', color: active ? 'var(--hv-mint-ink)' : 'var(--hv-ink)' }}>
            <span style={{ flex: 'none', marginTop: 2, width: 16, height: 16, borderRadius: 999, border: `1.5px solid ${active ? 'var(--brand,#13422C)' : 'var(--hv-line-2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {active && <span style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--brand,#13422C)' }} />}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, letterSpacing: '-0.01em' }}>{title}</span>
                <span style={{ display: 'block', marginTop: 3, fontSize: 12.5, lineHeight: 1.5, color: active ? 'var(--hv-mint-ink)' : 'var(--hv-body)' }}>{sub}</span>
            </span>
        </button>
    );

    return (
        <Modal open={open} onClose={close} label="Publish your site">
            {liveUrl ? (
                <div style={{ padding: '30px 26px 26px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <span style={{ width: 58, height: 58, borderRadius: 999, background: 'var(--hv-mint)', color: 'var(--hv-mint-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'hvRise 0.45s cubic-bezier(0.2,0.9,0.24,1) both' }}>
                        {Icons.check({ size: 26, sw: 2.6 })}
                    </span>
                    <h2 style={{ margin: '18px 0 0', fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 32, lineHeight: 1.05, letterSpacing: '-0.015em', color: 'var(--hv-ink)' }}>Your site is live</h2>
                    <p style={{ margin: '9px auto 0', maxWidth: 330, fontSize: 13.5, lineHeight: 1.6, color: 'var(--hv-body)' }}>Anyone with the link can open your site now.</p>

                    <div style={{ marginTop: 22, width: '100%', padding: 16, borderRadius: 16, background: 'var(--hv-card-2)', border: '1px solid var(--hv-line)', textAlign: 'left' }}>
                        <Eyebrow>LIVE URL</Eyebrow>
                        <div style={{ marginTop: 9, display: 'flex', alignItems: 'center', gap: 10 }}>
                            <a href={liveUrl} target="_blank" rel="noopener noreferrer" className="hv-h-underline" style={{ flex: 1, minWidth: 0, fontFamily: FONT_MONO, fontSize: 13, color: 'var(--hv-mint-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{liveUrl}</a>
                            <button type="button" onClick={() => void copyLive()} className="hv-h-mint-bd" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 7, height: 32, padding: '0 12px', borderRadius: 999, border: '1px solid var(--hv-line-2)', background: 'transparent', color: 'var(--hv-body)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                                {copied ? Icons.check({ size: 13, sw: 2.6 }) : Icons.copy({})}
                                {copied ? 'Copied' : 'Copy'}
                            </button>
                        </div>
                    </div>

                    <div style={{ marginTop: 22, width: '100%', display: 'flex', gap: 10 }}>
                        <GhostBtn onClick={() => onOpenChange(false)}>Close</GhostBtn>
                        <BrandBtn style={{ flex: 1.6 }} onClick={() => window.open(liveUrl, '_blank', 'noopener,noreferrer')}>
                            View site {Icons.arrow({})}
                        </BrandBtn>
                    </div>
                </div>
            ) : (
                <>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '26px 26px 0' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <h2 style={{ margin: 0, fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 32, lineHeight: 1.05, letterSpacing: '-0.015em', color: 'var(--hv-ink)' }}>{existing ? 'Republish your site' : 'Publish your site'}</h2>
                            <p style={{ margin: '9px 0 0', fontSize: 13.5, lineHeight: 1.6, color: 'var(--hv-body)' }}>
                                {existing ? 'Rebuild and redeploy the latest version of your site.' : 'Pick the web address people will use to open your site.'}
                            </p>
                        </div>
                        <CloseX onClick={close} />
                    </div>

                    <div style={{ padding: '22px 26px 26px' }}>
                        {existing && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
                                <Eyebrow>ADDRESS</Eyebrow>
                                {optionRow(mode === 'reuse', () => { setMode('reuse'); setSlug(existing.subdomain); setStatus('free'); setError(null); }, 'Keep the same address', `${existing.subdomain}.${appDomain} · version ${existing.version}`)}
                                {optionRow(mode === 'new', () => { setMode('new'); setSlug(''); setStatus('idle'); setError(null); }, 'Use a new address', `The old address (${existing.subdomain}.${appDomain}) will stop working.`)}
                            </div>
                        )}

                        <label style={{ display: 'flex', flexDirection: 'column', gap: 8, opacity: reusing ? 0.6 : 1 }}>
                            <Eyebrow>SUBDOMAIN</Eyebrow>
                            <span className="hv-composer" style={{ display: 'flex', alignItems: 'center', height: 52, padding: '0 4px 0 16px', borderRadius: 14, background: 'var(--hv-card-2)', border: `1px solid ${slugBorder}`, transition: 'border-color 0.18s ease, box-shadow 0.18s ease' }}>
                                <input
                                    value={slug}
                                    onChange={(e) => onSlug(e.target.value)}
                                    placeholder="your-site-name"
                                    spellCheck={false}
                                    disabled={reusing}
                                    style={{ flex: 1, minWidth: 0, border: 0, outline: 'none', background: 'transparent', color: 'var(--hv-ink)', fontFamily: FONT_MONO, fontSize: 14, letterSpacing: '-0.01em' }}
                                />
                                {status === 'checking' && <Spinner size={16} />}
                                {status === 'free' && !!slug && <span style={{ flex: 'none', color: 'var(--hv-leaf)', display: 'flex' }}>{Icons.check({ size: 17, sw: 2.6 })}</span>}
                                {status === 'taken' && <span style={{ flex: 'none', color: 'var(--hv-danger)', display: 'flex' }}>{Icons.x({})}</span>}
                                <span style={{ flex: 'none', padding: '0 12px', fontFamily: FONT_MONO, fontSize: 13, color: 'var(--hv-muted)' }}>.{appDomain}</span>
                            </span>
                            <span style={{ fontSize: 12.5, fontWeight: 600, color: slugMsgFg }}>{slugMsg[status]}</span>
                        </label>

                        {onOpenDomains && (
                            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 14, border: '1px dashed var(--hv-line-2)', background: 'var(--hv-card-2)' }}>
                                <span style={{ flex: 'none', display: 'flex', color: 'var(--hv-mint-ink)' }}>{Icons.globe({ size: 17 })}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <Eyebrow>CUSTOM DOMAIN</Eyebrow>
                                    <div style={{ marginTop: 3, fontSize: 12.5, lineHeight: 1.5, color: 'var(--hv-body)' }}>
                                        {existing?.customDomain
                                            ? <>Live at <span style={{ fontFamily: FONT_MONO, color: 'var(--hv-ink)' }}>{existing.customDomain}</span> as well.</>
                                            : 'Use your own .com or .id, or buy one here.'}
                                    </div>
                                </div>
                                <button type="button" data-track="publish_domains" onClick={onOpenDomains} className="hv-h-mint-bd" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 999, border: '1px solid var(--hv-line-2)', background: 'transparent', color: 'var(--hv-body)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                                    {existing?.customDomain ? 'Manage' : 'Set up'}
                                </button>
                            </div>
                        )}

                        <div style={{ marginTop: 18, display: 'flex', gap: 13, padding: 16, borderRadius: 16, background: canAfford ? 'var(--hv-mint)' : 'var(--hv-card-2)', color: canAfford ? 'var(--hv-mint-ink)' : 'var(--hv-body)' }}>
                            <span style={{ flex: 'none', marginTop: 1, display: 'flex' }}>{Icons.globe({})}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em' }}>{existing ? 'Publish the latest version' : 'Publish it live'}</div>
                                <div style={{ marginTop: 5, fontSize: 12.5, lineHeight: 1.6 }}>
                                    Your site will be open to anyone at <strong style={{ fontWeight: 700 }}>{preview}</strong>. Publishing costs {PUBLISH_COST} credits. You have {fmtCredits(credits)} credits.
                                </div>
                            </div>
                        </div>

                        {!canAfford && (
                            <DangerNote style={{ marginTop: 12 }}>Not enough credits. You need {fmtCredits(PUBLISH_COST - credits)} more to publish.</DangerNote>
                        )}
                        {error && <DangerNote style={{ marginTop: 12 }}>{error}</DangerNote>}

                        <div style={{ marginTop: 22, display: 'flex', gap: 10 }}>
                            <GhostBtn onClick={close} disabled={publishing}>Cancel</GhostBtn>
                            {canAfford ? (
                                <BrandBtn style={{ flex: 1.6 }} disabled={!ready} data-track="publish_confirm" onClick={() => void confirm()}>
                                    {publishing ? 'Publishing…' : existing ? 'Confirm republish' : 'Confirm publish'}
                                    {publishing ? <Spinner size={15} light /> : Icons.arrow({})}
                                </BrandBtn>
                            ) : (
                                <BrandBtn style={{ flex: 1.6 }} data-track="publish_top_up" onClick={() => { onOpenChange(false); onTopUp(); }}>
                                    Top up credits {Icons.arrow({})}
                                </BrandBtn>
                            )}
                        </div>
                    </div>
                </>
            )}
        </Modal>
    );
};
